import { authApi } from './adminApi';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    refreshToken?: string;
    user?: User;
  };
}

class AuthService {
  private static readonly ACCESS_TOKEN_KEY = 'adminAccessToken';
  private static readonly REFRESH_TOKEN_KEY = 'adminRefreshToken';
  private static readonly USER_KEY = 'adminUser';

  // Login method
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await authApi.login(credentials.email, credentials.password);
      
      if (response.success && response.data) {
        // Store tokens and user data
        this.setAccessToken(response.data.token);
        
        if (response.data.refreshToken) {
          this.setRefreshToken(response.data.refreshToken);
        }
        
        if (response.data.user) {
          this.setUser(response.data.user);
        }
      }

      return response as LoginResponse;
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        throw new Error('Invalid credentials');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(error.response?.data?.message || 'Login failed');
      }
    }
  }

  // Refresh token method
  async refreshToken(): Promise<string | null> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // For now, since admin API doesn't have refresh endpoint, 
      // we'll redirect to login
      this.logout();
      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      return null;
    }
  }

  // Logout method
  async logout(): Promise<void> {
    try {
      // Call logout API if available
      await authApi.logout();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local storage
      this.clearAuthData();
    }
  }

  // Token management methods
  setAccessToken(token: string): void {
    localStorage.setItem(AuthService.ACCESS_TOKEN_KEY, token);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(AuthService.REFRESH_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(AuthService.REFRESH_TOKEN_KEY);
  }

  // User management methods
  setUser(user: User): void {
    localStorage.setItem(AuthService.USER_KEY, JSON.stringify(user));
  }

  getUser(): User | null {
    const userData = localStorage.getItem(AuthService.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  // Alias for backward compatibility
  getCurrentUser(): User | null {
    return this.getUser();
  }

  // Verify session (alias for verifyToken)
  async verifySession(): Promise<boolean> {
    return this.verifyToken();
  }

  // Authentication status
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      // Simple token validation - in a real app, you might want to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  // Verify token with server
  async verifyToken(): Promise<boolean> {
    try {
      const token = this.getAccessToken();
      if (!token) return false;

      // Since we don't have a verify endpoint, we'll just check local token
      return this.isAuthenticated();
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  }

  // Clear all authentication data
  private clearAuthData(): void {
    localStorage.removeItem(AuthService.ACCESS_TOKEN_KEY);
    localStorage.removeItem(AuthService.REFRESH_TOKEN_KEY);
    localStorage.removeItem(AuthService.USER_KEY);
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.role === role;
  }

  // Check if user is admin
  isAdmin(): boolean {
    return this.hasRole('admin') || this.hasRole('super_admin');
  }

  // Get user role
  getUserRole(): string | null {
    const user = this.getUser();
    return user?.role || null;
  }

  // Initialize auth state (call this on app startup)
  async initialize(): Promise<boolean> {
    try {
      const isAuthenticated = await this.verifyToken();
      if (!isAuthenticated) {
        this.clearAuthData();
        return false;
      }
      return true;
    } catch (error) {
      console.error('Auth initialization error:', error);
      this.clearAuthData();
      return false;
    }
  }
}

// Export singleton instance
export default new AuthService();
